import React, {
  FC,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import debounce from "lodash/debounce";
import Loader from "ui/Loader/Loader";
import Autocomplete from "common/Autocomplete/Autocomplete";
import SearchRequestPreview from "common/SearchRequestPreview/SearchRequestPreview";
import Location from "assets/icons/location.svg";
import CloseRound from "assets/icons/close-round.svg";
import { useStore } from "store";
import { useApi } from "services/ApiService";
import { useBreakpoint } from "helpers/hooks";
import styles from "./RegionAutocomplete.module.scss";

type Props = {
  region: Entity.Region;
  onChange: (region: Entity.Region) => void;
  onInputChange?: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  className?: string;
  classNameTextField?: string;
  classNameTextFieldWrap?: string;
};

const RegionAutocomplete: FC<Props> = ({
  region,
  onChange,
  onInputChange,
  onFocus,
  onBlur,
  className,
  classNameTextField,
  classNameTextFieldWrap,
}: Props) => {
  const { notifications } = useStore();
  const breakpoint = useBreakpoint();
  const {
    methods: { search },
  } = useApi();
  const refInput = useRef(null);
  const [regions, setRegions] = useState<Array<Entity.GeoObject>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [value, setValue] = useState<string>(region ? region.name : "");
  const [focused, setFocused] = useState<boolean>(false);
  const [canShowPopup, setCanShowPopup] = useState<boolean>(false);

  const onInputChangeHandler = useCallback(
    event => {
      setValue(event.target.value);
      setCanShowPopup(true);
      onInputChange && onInputChange(event.target.value);
    },
    [onInputChange]
  );

  const reset = () => {
    setValue("");
    setFocused(true);
    setCanShowPopup(false);
    onChange && onChange(null);
    setFocus();
  };

  const setFocus = () => {
    if (refInput.current) {
      refInput.current.focus();
    }
  };

  const select = region => {
    setFocused(false);
    setCanShowPopup(false);
    onChange && onChange(region);
    onBlur && onBlur();
  };

  const trimValue = value => {
    return value.trim().replace(/ +/g, " ");
  };

  const trimmedValue = useMemo(() => {
    return trimValue(value);
  }, [value]);

  const requestRegions = useMemo(
    () =>
      debounce(async value => {
        setIsLoading(true);
        const { data, errors } = await search.suggestRegion({
          query: trimValue(value),
        });
        if (data && data.suggestRegion) {
          setRegions(data.suggestRegion);
        }
        if (errors) {
          errors.forEach(err => {
            notifications.show({
              type: "toast",
              props: {
                title: "Ошибка при получении регионов",
                children: err.message,
              },
            });
          });
        }
        setIsLoading(false);
      }, 600),
    [notifications, search]
  );

  const onKeyDown = useCallback(
    event => {
      if (regions.length === 0) {
        requestRegions(event.target.value);
      }
    },
    [regions.length, requestRegions]
  );

  const valueNotEqual = !region || region.name !== value;

  let postfix =
    value && valueNotEqual && focused ? (
      <button className={styles.regionAutocompleteHovering} onClick={reset}>
        <CloseRound />
      </button>
    ) : (
      <Location />
    );

  if (isLoading) {
    postfix = <Loader small />;
  }

  const suggestions = useMemo(() => {
    return regions
      .filter(r => r.name.toLowerCase().includes(trimmedValue.toLowerCase()))
      .map((s, index) => {
        return (
          <div
            className={styles.regionAutocompleteSuggestion}
            key={`key-${index}`}
            onClick={() => select(s)}
          >
            {s.name}
          </div>
        );
      });
  }, [regions, select, trimmedValue]);

  const showSuggestion =
    focused && canShowPopup && regions.length > 0 && !isLoading;

  useEffect(() => {
    if (focused) {
      setFocus();
    }
  }, [focused]);

  useEffect(() => {
    setValue(region ? region.name : "");
  }, [region]);

  useEffect(() => {
    return () => {
      requestRegions.cancel();
    };
  }, [requestRegions]);

  return (
    <Autocomplete
      ref={refInput}
      autoFocus={breakpoint && breakpoint !== "mobile"}
      value={value}
      onKeyDown={onKeyDown}
      onChange={onInputChangeHandler}
      onFocus={() => {
        if (!focused) {
          setFocused(true);
          onFocus && onFocus();
        }
      }}
      postfix={postfix}
      placeholder="Название региона"
      suggestions={
        showSuggestion
          ? suggestions.length > 0
            ? suggestions
            : [
                <SearchRequestPreview
                  key="not-found"
                  text="По вашему запросу ничего не найдено. Проверьте корректность ввода или введите другой регион"
                  sadFace
                />,
              ]
          : null
      }
      className={className}
      classNameTextField={classNameTextField}
      classNameTextFieldWrap={classNameTextFieldWrap}
    />
  );
};

export default RegionAutocomplete;
